import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosError, AxiosResponse } from 'axios';

import { getUpcomingWeekdays, getPreviousWeekdays } from '../../../components/wrapperEventManager/tableUtils.component';

import { TableCreation } from 'interfaces/types.interface';
import { EventManager, EventManagerWithPosition } from 'interfaces/eventManager.interface';

import { CommonGlobalService, dayjs, LoggerService, RootState, usePersistantState, UUID } from '@umanforge/common';

import { KEY } from 'constants/local_storage_key.constant';
import { DIVIDE_FOR_WEEK, ZOOM_LEVEL } from 'constants/eventManager.constant';

import { GlobalService } from 'services/global.service';
import { EventManagerService } from 'services/eventManagers.service';
import { ToastService } from 'services/toast.service';

export const useEventManagerNavigationBar = (
  eventManager: EventManager | null,
  onEventManagersChanged: ( eventManagers: EventManagerWithPosition[] ) => void,
  eventManagerId:UUID | undefined,
  isArchived: boolean,
  viewMod: boolean,
  calendarItem: number,
  setCalendarItem: React.Dispatch<React.SetStateAction<number>>,
  days: dayjs.Dayjs[],
  setDays: React.Dispatch<React.SetStateAction<dayjs.Dayjs[]>>,
  refreshEventManager: () => Promise<void>,
  onSectorIdChanged: ( newSectorId: UUID | undefined ) => void
) => {
  const toastService: ToastService = new ToastService( 'EventManagerContent' );
  const logger: LoggerService = new LoggerService( 'EventManagerContent' );

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const navigate = useNavigate();

  //-------------- useState ---------------\\
  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ eventManagers, setEventManagers ] = useState<EventManagerWithPosition[]>( [] );
  const [ weekIsActual, setWeekIsActual ] = useState<boolean>( true );

  const [ isPopupVisible, setIsPopupVisible ] = useState( false );
  const [ isOpenVisualisationModal, setIsOpenVisualisationModal ] = useState( false );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  //-------------- useEffect --------------\\
  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender && selectedSectorId ) {
      updateEventManagers( selectedSectorId );
    }
  }, [ selectedSectorId, viewMod ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      onEventManagersChanged( eventManagers );
    }
  }, [ eventManagers ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      const today = dayjs();

      const isTodayInDays = days.some( day => {
        const dayDate = day;
        return (
          dayDate.year() === today.year() &&
          dayDate.month() === today.month() &&
          dayDate.date() === today.date()
        );
      } );

      setWeekIsActual( isTodayInDays );
    }
  }, [ days ] );

  //-------------- Functions --------------\\
  const updateEventManagers = async ( sectorId: UUID ) => {
    await getUpdatedEventManagers( sectorId )
      .then( setEventManagers );
    setIsFirstRender( false );
  };

  const getUpdatedEventManagers = async ( sectorId: UUID | undefined ) => {
    const updatedEventManagers: EventManagerWithPosition[] = [];
    try {
      if ( sectorId ) {
        if ( viewMod ) {
          if ( eventManager ) {
            updatedEventManagers.push( { id: eventManager.id, name: eventManager.name, position: 0 } );
          }
        } else {
          updatedEventManagers.push( ...await EventManagerService.getTablesBySector( sectorId, token, dispatch, onAxiosError, onAxiosWarning ) );
        }
      }
    } catch ( err ) {
      CommonGlobalService.onCatchError( err as Error, logger, onAxiosError );
    } finally {
      return updatedEventManagers;
    }
  };

  const updateTable = async ( postEventManager: TableCreation ): Promise<UUID | null> => {
    let isUpdate = false;
    if ( eventManagerId && selectedSectorId ) {
      isUpdate = await EventManagerService.updateEventManager( eventManagerId, postEventManager, token, dispatch );
      if ( isUpdate ) {
        toastService.showSuccess( 'translation:event_manager_updated' );
        await refreshEventManager();
      }
    }
    return isUpdate ? ( eventManagerId ?? null ) : null;
  };

  const deleteTable = async ( EvenManagerId: string ): Promise<boolean> => {
    sessionStorage.setItem( KEY.DELETED_EVENT_MANAGER_ID, EvenManagerId );
    const isDeleted = await EventManagerService.deleteTable( EvenManagerId, token, dispatch );
    if ( !isDeleted ) {
      sessionStorage.removeItem( KEY.DELETED_EVENT_MANAGER_ID );
    }
    return isDeleted;
  };

  const canNavigateToFuture = () => {
    if ( !isArchived ) {
      return true;
    }
    if ( days.length === 0 || !eventManager ) {
      return false;
    }

    let count = calendarItem;
    if ( calendarItem > ZOOM_LEVEL.TEN_DAYS ) {
      const numberOfDayWeek = Object.values( eventManager.daysToShow ).filter( day => { return day === true; } ).length;
      const numberOfWeekToSee = calendarItem / DIVIDE_FOR_WEEK;
      count = numberOfDayWeek * numberOfWeekToSee;
    }

    const lastDay = days[ ( days.length <= count ? ( days.length - 1 ) : count ) ].add( 1, 'day' );
    const nextWeekdays = getUpcomingWeekdays( lastDay, count, eventManager.daysToShow );

    if ( nextWeekdays.length === 0 ) {
      return false;
    }

    const today = dayjs().startOf( 'day' );

    const hasDaysBeforeToday = days.some( d => { return d.startOf( 'day' ).isBefore( today ); } );

    const firstNextDay = nextWeekdays[ 0 ].startOf( 'day' );
    const nextIsNotAfterToday = !firstNextDay.isAfter( today );

    return nextWeekdays.length > 0 && ( hasDaysBeforeToday || nextIsNotAfterToday );
  };

  //--------------- Events ----------------\\
  const onClickNavigationIndicators = () => {
    navigate( '/Indicators' );
  };

  const onClickPreviousDays = () => {
    if ( eventManager == undefined || days.length === 0 ) {
      return;
    }
    let count = calendarItem;
    if ( calendarItem > ZOOM_LEVEL.TEN_DAYS ) {
      const numberOfDayWeek = Object.values( eventManager.daysToShow ).filter( day => { return day === true; } ).length;
      const numberOfWeekToSee = calendarItem / DIVIDE_FOR_WEEK;
      count = numberOfDayWeek * numberOfWeekToSee;
    }
    const firstDay = days[ 0 ];
    let previousWeekdays = getPreviousWeekdays( firstDay, count, eventManager.daysToShow );
    if ( !isArchived && previousWeekdays[ 0 ] ) {
      const today = dayjs().startOf( 'day' );
      if ( previousWeekdays[ 0 ].startOf( 'day' ).isBefore( today ) ) {
        previousWeekdays = getUpcomingWeekdays( today, count, eventManager.daysToShow );
      }
    }

    setDays( [ ...previousWeekdays ] );
  };

  const onClickNextDays = () => {
    if ( eventManager == undefined || days.length === 0 ) {
      return;
    }

    let count = calendarItem;
    if ( calendarItem > ZOOM_LEVEL.TEN_DAYS ) {
      const numberOfDayWeek = Object.values( eventManager.daysToShow ).filter( day => { return day === true; } ).length;
      const numberOfWeekToSee = calendarItem / DIVIDE_FOR_WEEK;
      count = numberOfDayWeek * numberOfWeekToSee;
    }

    const lastDay = days[ ( days.length <= count ? ( days.length - 1 ) : count ) ].add( 1, 'day' );
    const nextWeekdays = getUpcomingWeekdays( lastDay, count, eventManager.daysToShow );

    if ( isArchived && nextWeekdays.length > 0 ) {
      const today = dayjs().startOf( 'day' );
      const firstNextDay = nextWeekdays[ 0 ].startOf( 'day' );
      if ( firstNextDay.isAfter( today ) ) {
        setDays( getUpcomingWeekdays( today, count, eventManager.daysToShow ) );
        return;
      }
    }

    setDays( [ ...nextWeekdays ] );
  };

  const onClickZoomIn = () => {
    if ( eventManager ) {
      const values = Object.values( ZOOM_LEVEL );
      let closest: number | null = null;
      values.forEach( value => {
        if ( value < calendarItem ) {
          closest = value;
        }
      } );
      const result = closest ?? calendarItem;
      const day = days.length >= 1 ? days[ 0 ] : dayjs();
      let count = result;
      if ( result > ZOOM_LEVEL.TEN_DAYS ) {
        const numberOfDayWeek = Object.values( eventManager.daysToShow ).filter( dayToShow => { return dayToShow === true; } ).length;
        const numberOfWeekToSee = result / DIVIDE_FOR_WEEK;
        count = numberOfDayWeek * numberOfWeekToSee;
      }
      setDays( getUpcomingWeekdays( day, count, eventManager.daysToShow ) );
      setCalendarItem( result );
    }
  };

  const onClickZoomOut = () => {
    if ( eventManager ) {
      const values = Object.values( ZOOM_LEVEL ).reverse();
      let closest: number | null = null;
      values.forEach( value => {
        if ( value > calendarItem ) {
          closest = value;
        }
      } );
      const result = closest ?? calendarItem;
      const day = days.length >= 1 ? days[ 0 ] : dayjs();
      let count = result;
      if ( result > ZOOM_LEVEL.TEN_DAYS ) {
        const numberOfDayWeek = Object.values( eventManager.daysToShow ).filter( dayToShow => { return dayToShow === true; } ).length;
        const numberOfWeekToSee = result / DIVIDE_FOR_WEEK;
        count = numberOfDayWeek * numberOfWeekToSee;
      }
      setDays( getUpcomingWeekdays( day, count, eventManager.daysToShow ) );
      setCalendarItem( result );
    }
  };

  const onSectorChanged = ( sectorId: UUID | undefined ) => {
    setSelectedSectorId( sectorId );
    onSectorIdChanged( sectorId );
  };

  const onAxiosWarning = ( warn : AxiosResponse ) => {
    GlobalService.onAxiosWarning( warn );
  };

  const onAxiosError = ( err : AxiosError ) => {
    GlobalService.onAxiosError( err, dispatch );
  };


  //---------------- Front ----------------\\
  return {
    onSectorChanged,
    onAxiosWarning,
    onAxiosError,
    eventManagers,
    setIsPopupVisible,
    isPopupVisible,
    isOpenVisualisationModal,
    setIsOpenVisualisationModal,
    weekIsActual,
    viewMod,
    onClickPreviousDays,
    onClickZoomIn,
    onClickZoomOut,
    canNavigateToFuture,
    onClickNextDays,
    onClickNavigationIndicators,
    updateTable,
    deleteTable
  };
};